import { useState, useContext } from 'react'
import i from 'i18next'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { State } from 'components/Layout'
import Logo from 'components/Logo'
import { ALL_PAGES, Main } from 'components/Nav'
import Forward from 'components/svg/Forward'
import { cvgTrack } from 'components/analytics/Converge'

export default function Footer() {
	const [ email, setEmail ] = useState<string>( '' )
	const [ done, setDone ] = useState( false )
	const { setFlyout } = useContext( State )

	const handleSubmit = e => {
		e.preventDefault()

		const body = new FormData()
		body.append( 'g', 'MsdPH7' )
		body.append( 'email', email )

		fetch( 'https://manage.kmail-lists.com/subscriptions/subscribe', {
			method: 'post',
			mode: 'no-cors',
			body,
		}).then( () => setDone( true ) )

		window._learnq = window._learnq || []
		window._learnq.push( [ 'identify', { '$email': email }] )
		window.metrilo?.identify( email )
		cvgTrack({
			eventName: 'Signed Up For Newsletter',
			profileProperties: { '$email': email },
			aliases: [ `urn:email:${email}` ],
		})
	}

	return (
		<div>
			<motion.footer
				className="container"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 1 }}
			>
				<div>
					<div style={{ display: 'flex', flexDirection: 'column' }}>
						<Link href="/" legacyBehavior><a aria-label="Ray Logo"><Logo /></a></Link>
						<div style={{ marginTop: 'auto', paddingTop: 60 }}>
						This website is developed with the support of:
						</div>
						<img src="/Flanders IT.png" width={180} height={108} />
					</div>
					<div className="content">
						{done ? (
							<div>
								<h2>{i.t( 'footer.signup.done.header' )}</h2>
								<p>{i.t( 'footer.signup.done.text' )}</p>
							</div>
						) : (
							<form onSubmit={handleSubmit}>
								<input type="hidden" name="g" value="MsdPH7" />
								<h2>{i.t( 'footer.signup.header' )}</h2>
								<div className="input-button">
									<input
										aria-label={i.t( 'footer.signup.placeholder' )}
										type="email"
										value={email}
										onChange={e => setEmail( e.target.value )}
										placeholder={i.t( 'footer.signup.placeholder' )}
									/>
									<button aria-label="Signup">
										<Forward />
									</button>
								</div>
							</form>
						)}
						<nav>
							<Main
								links={ALL_PAGES.map( ( href ) => ({
									href,
									text: i.t( `nav.${href.substr( 1 )}` ),
								}) )}
							/>
							<div>
								<ul>
									<li>
										<a
											href="/shipping"
											onClick={e => {
												e.preventDefault()
												setFlyout( 'shipping' )
											}}
										>
											{i.t( 'footer.link.shipping' )}
										</a>
									</li>
								</ul>
								{i.t( 'footer.links.boring', { postProcess: 'markdown-jsx' })}
							</div>
							<div>
								{i.t( 'footer.links.social', { postProcess: 'markdown-jsx' })}
							</div>
						</nav>
					</div>
				</div>
				<div>
					<small>
						<span>
							{i.t( 'footer.copyright', { postProcess: 'markdown-jsx' })}
						</span>
						<br />
						<span>
							{i.t( 'footer.address', { postProcess: 'markdown-jsx' })}
						</span>
					</small>
					<small>
						{i.t( 'footer.ollie', { postProcess: 'markdown-jsx' })}
					</small>
				</div>
			</motion.footer>
			<style jsx>{`
				div :global( footer.container ) {
					margin-top: min(30vh, 150px);
					padding-bottom: 5%;
				}
				div :global( footer > div ) {
					display: flex;
					justify-content: space-between;
					gap: 40px;
				}
				div :global( footer small + small ) {
					margin-top: 2em;
				}
				.content {
					width: calc( ( 100%/12 ) * 7 );
				}
				h2 {
					margin-bottom: 2em;
					font-size: 35px;
					line-height: 1.49;
				}
				input {
					width: 100%;
				}
				input::placeholder {
					color: black;
				}
				.input-button {
					position: relative;
				}
				.input-button button {
					position: absolute;
					right: 0;
					top: 5px;
					padding: 10px 0 10px 40px;
					background-color: transparent;
					border: none;
					outline: none;
					cursor: pointer;
				}
				nav {
					margin-top: 10vh;
					display: flex;
					align-items: flex-start;
					justify-content: space-between;
					text-transform: lowercase;
				}
				nav :global( ul ) {
					margin-right: 30px;
				}
				nav :global( li ) {
					margin-bottom: .5em;
				}
				nav :global( li::after ) {
					display: none;
				}
				small {
					font-size: 18px;
					line-height: 1.89;
				}
				small :global( p ) {
					display: inline;
					line-height: 24px;
				}
				small :global( a::after ) {
					bottom: 2px;
				}

				@media ( min-width: 900px ) {
					div :global( footer > div:first-child ) {
						margin-bottom: 15vh;
					}
				}
				@media ( max-width: 900px ) {
					div :global( footer > div ) {
						flex-wrap: wrap;
					}
					.content {
						width: 100%;
						margin-top: 70px;
					}
					h2 {
						font-size: 25px;
  					line-height: 1.32;
					}
					nav {
						flex-wrap: wrap;
					}
					nav > :global( * ) {
						margin-bottom: 50px;
					}
				}
			`}</style>
		</div>
	)
}
